/**
 * SyPI scheduling / draft management.
 *
 * - `setScheduledDraftConfig` loads an existing scheduled/draft config into
 *   the editor and routes to its first modified section.
 * - `saveScheduledDraftedConfig` commits the current changes as a scheduled
 *   or drafted config, spanning multiple release versions when needed. If an
 *   initial draft/scheduled was being edited, its predecessor is deleted.
 * - `deleteSchecduledDraft` (note historical typo, preserved as a public
 *   API contract) removes a scheduled or drafted config.
 * - `updateFMLinks` reconciles pending File Manager links declared in the
 *   config by creating the location records via `filesAdapter`.
 *
 * Adopts `runAsyncAction` in `deleteSchecduledDraft` (clean spinner + error
 * pattern; the report object generated inside is still returned to the
 * caller via the ok/error result).
 */

import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import { getStore } from "@store";
import sypiAdapter from "@features/sypi/adapters";
import filesAdapter from "@features/files/adapters";
import { isEmptyObject } from "@utils/object-helpers";
import { isObject } from "@utils/input-helpers";
import { toSentenceCase } from "@utils/string-helpers";
import {
  configSchedulerEnrichment,
  getModifiedSections,
} from "@features/sypi/_utils/sypi-interface-helpers";
import { SYPI_S3 } from "@actions/types";
import { runAsyncAction } from "@utils/runAsyncAction";
import {
  showSpinner,
  stopSpinner,
  closeDock,
  clearChanges,
  clearLog,
} from "@actions/global";
import {
  setSypiConfigSaveState,
  setReleaseVersion,
  setInitialReleaseVersion,
  setDefaultReleaseVersion,
  loadScheduledDraft,
  loadInitialScheduledDraft,
  loadScheduledDraftSet,
  resetSypiSchedulerSetUp,
  clearFMLinks,
} from "@features/sypi/actions";
import {
  SYPI_SECTIONS,
  loadSypiVersionDetails,
} from "./sypi-shared";
import { fetchSypi } from "./sypi-init";
import { fetchScheduledDraftConfig } from "./sypi-history";

export const setScheduledDraftConfig = async (
  paramValue,
  isScheduled,
  release_version,
  store = getStore()
) => {
  let finalReport = null,
    displayStatus = "";
  store.dispatch(setSypiConfigSaveState("edit"));
  store.dispatch(setReleaseVersion(release_version));
  store.dispatch(setInitialReleaseVersion(release_version));
  store.dispatch(setDefaultReleaseVersion(release_version));
  await fetchSypi(release_version, true);
  store.dispatch(showSpinner());
  const paramConfig = isScheduled
    ? { live_schedule_date: paramValue, release_version: release_version }
    : { name: paramValue, release_version: release_version };
  let fetchResult = await fetchScheduledDraftConfig(paramConfig, isScheduled);
  let { apiError, configSchedules } = fetchResult;
  if (apiError && apiError.details) {
    finalReport = {
      type: "ALERT_DOCK",
      title: `Loading the ${toSentenceCase(
        apiError.details
      )} draft was successful. `,
      message:
        "Please contact the application development team for assistance.",
    };
  } else {
    if (configSchedules && configSchedules.length) {
      const sypiSchedulerConfig = configSchedules.find(
        (config) => config.release_version === release_version
      );
      store.dispatch(loadScheduledDraft(sypiSchedulerConfig));
      store.dispatch(loadInitialScheduledDraft(sypiSchedulerConfig));
      if (
        sypiSchedulerConfig &&
        sypiSchedulerConfig.schemaPath &&
        !isEmptyObject(sypiSchedulerConfig.schemaPath)
      ) {
        const { sypiS3 } = sypiSchedulerConfig.schemaPath;
        loadSypiVersionDetails(SYPI_S3, sypiS3);
        displayStatus =
          sypiSchedulerConfig.status === "scheduled" ? "Schedule" : "Draft";
      }
      const { sectionsModified } = sypiSchedulerConfig;
      const redirectTo =
        sectionsModified && sectionsModified.length
          ? Object.keys(SYPI_SECTIONS).reduce((accSection, section) => {
              if (
                isEmptyObject(accSection) &&
                sectionsModified.includes(section)
              ) {
                accSection = { section: SYPI_SECTIONS[section] };
              }
              return accSection;
            }, {})
          : { section: "sypi-content" };
      finalReport = {
        type: "TOAST_MESSAGE",
        message: `${displayStatus} changes loaded `,
        redirect: `/${redirectTo.section}`,
      };
    }
  }
  store.dispatch(stopSpinner());
  return finalReport;
};

export const saveScheduledDraftedConfig = async (
  original_changeDict = {},
  store = getStore()
) => {
  const storeContents = store.getState();
  let finalReport = null;
  const { config, sypiNewView, sypiConfigScheduler, sypiSchema } =
    storeContents;
  const { scheduleDraft, initialScheduleDraft, otherConfigsScheduledDrafts } =
    sypiConfigScheduler;
  const { activeReleaseVersion, selectedReleaseVersions } = sypiSchema;
  const { changeDict } = sypiNewView;
  const isScheduled = scheduleDraft.status === "scheduled";

  const modifiedSections = getModifiedSections(changeDict);
  const updatedScheduledDraft = configSchedulerEnrichment(
    storeContents,
    isScheduled,
    modifiedSections
  );
  let scheduledDraftResults = {};
  if (isScheduled) {
    for (let index in selectedReleaseVersions) {
      let release_version = selectedReleaseVersions[index];
      if (release_version === activeReleaseVersion) {
        const results = await sypiAdapter.saveScheduledConfig(
          updatedScheduledDraft
        );
        scheduledDraftResults = {
          ...scheduledDraftResults,
          [release_version]: results,
        };
      } else {
        let { summaryView, config } =
          otherConfigsScheduledDrafts[release_version];
        let hasChanged = false;
        let strPaths =
          Object.keys(original_changeDict).length === 0
            ? changeDict
            : original_changeDict;
        Object.keys(strPaths).forEach((strPath) => {
          let oldValue = get(config, strPath);
          let newValue = changeDict[strPath];
          if (has(config, strPath)) {
            if (
              typeof newValue === typeof oldValue &&
              isObject(newValue) === isObject(oldValue) &&
              newValue !== oldValue
            ) {
              set(config, strPath, newValue);
              hasChanged = true;
            }
          }
        });

        let importedConfig = {
          newConfig: config,
          release_version: release_version,
          newSummaryView: summaryView,
          changeDict: !isEmptyObject(original_changeDict)
            ? original_changeDict
            : changeDict,
        };

        if (hasChanged) {
          const modifiedSections = getModifiedSections(strPaths);
          let newUpdatedScheduled = configSchedulerEnrichment(
            storeContents,
            isScheduled,
            modifiedSections,
            importedConfig
          );
          let newResults = await sypiAdapter.saveScheduledConfig(
            newUpdatedScheduled
          );
          scheduledDraftResults = {
            ...scheduledDraftResults,
            [release_version]: newResults,
          };
        }
      }
    }
  } else {
    scheduledDraftResults = await sypiAdapter.saveDraftedConfig(
      updatedScheduledDraft
    );
  }

  for (let release_version in scheduledDraftResults) {
    let results = scheduledDraftResults[release_version];
    let { apiError, type, error_elements } = results;

    if (apiError && apiError.details) {
      finalReport = {
        type: "ALERT_DOCK",
        title: `Saving the ${release_version} draft was unsuccessful. `,
        message:
          "Please contact the application development team for assistance.",
      };
    } else {
      if (
        initialScheduleDraft.status === "draft" &&
        ((initialScheduleDraft.name &&
          initialScheduleDraft.name !== scheduleDraft.name) ||
          initialScheduleDraft.status !== scheduleDraft.status)
      ) {
        await deleteSchecduledDraft(
          {
            idTarget: initialScheduleDraft.name,
            release_version:
              initialScheduleDraft.release_version ||
              initialScheduleDraft.releaseVersion,
          },
          "draft"
        );
      } else if (
        initialScheduleDraft.status === "scheduled" &&
        initialScheduleDraft.liveScheduleDate &&
        initialScheduleDraft.liveScheduleDate !==
          scheduleDraft.liveScheduleDate &&
        scheduleDraft.status === "scheduled"
      ) {
        await deleteSchecduledDraft(
          {
            idTarget: initialScheduleDraft.liveScheduleDate,
            release_version:
              initialScheduleDraft.release_version ||
              initialScheduleDraft.releaseVersion,
          },
          "scheduled"
        );
      }
      store.dispatch(closeDock());
      store.dispatch(clearChanges());
      store.dispatch(clearLog());
      finalReport = {
        type: "TOAST_MESSAGE",
        message: `Saving the config was successful. `,
        redirect: "/sypi-config-schedule",
      };
    }
  }
  return finalReport;
};

/**
 * Delete a scheduled / draft / completed config. Uses `runAsyncAction` for
 * the shared error surface; the finalReport object is returned to the
 * caller via `runAsyncAction`'s `onSuccess` capture.
 *
 * Note: the historical name uses "Schecduled" (typo). Preserved as-is —
 * external consumers (`sypi-containers/*`, sypi-import) reference the
 * misspelled name.
 */
export const deleteSchecduledDraft = async (
  keyTarget,
  deleteStatus,
  store = getStore()
) => {
  let capturedReport = null;
  await runAsyncAction(
    store,
    async () => {
      const storeContents = store.getState();
      const deleteConfig =
        deleteStatus === "draft"
          ? {
              name: keyTarget.idTarget,
              release_version:
                keyTarget.release_version || keyTarget.releaseVersion,
            }
          : {
              live_schedule_date: keyTarget.idTarget,
              release_version:
                keyTarget.release_version || keyTarget.releaseVersion,
              revert_history_path: keyTarget?.revert_history_path,
            };
      let { sypiConfigScheduler } = storeContents;
      const isScheduled = deleteStatus === "scheduled";
      let result = isScheduled
        ? await sypiAdapter.deleteScheduledConfig(deleteConfig)
        : deleteStatus === "draft"
        ? await sypiAdapter.deleteDraftConfig(deleteConfig)
        : {};

      let { success } = result;
      if (!success) {
        capturedReport = {
          type: "ALERT_DOCK",
          title: `Deleting the ${deleteStatus} was unsuccessful. `,
          message:
            "Please contact the application development team for assistance.",
        };
      } else if (success) {
        let { scheduleDraftSet } = sypiConfigScheduler;
        const updatedScheduleDraftSet = scheduleDraftSet.filter((config) =>
          config.status !== deleteStatus
            ? true
            : isScheduled
            ? config.liveScheduleDate !== deleteConfig.live_schedule_date ||
              (config.liveScheduleDate === deleteConfig.live_schedule_date &&
                config.release_version !== deleteConfig.release_version)
            : config.status === "draft" && config.name !== deleteConfig.name
        );

        store.dispatch(loadScheduledDraftSet(updatedScheduleDraftSet));
        store.dispatch(resetSypiSchedulerSetUp());
        capturedReport = {
          type: "TOAST_MESSAGE",
          message: `Deleting ${deleteStatus} was successful`,
        };
      }
      store.dispatch(closeDock());
    },
    {
      spinner: false, // caller (confirmSypiConfigAction) already dispatched showSpinner
      errorAction: "delete",
      errorSubject: `${deleteStatus} config`,
    }
  );
  return capturedReport;
};

/**
 * Reconciles pending File Manager links declared in the sypi config by
 * creating each location through `filesAdapter`. Reports either a
 * TOAST_MESSAGE on full success or an ALERT_DOCK with a per-line error
 * summary.
 */
export const updateFMLinks = async (store = getStore()) => {
  const storeContents = store.getState();
  let { sypiNewView } = storeContents;
  let { fmLink, fmLinkBucket } = sypiNewView;
  let finalReport = {};
  const values = Object.values(fmLink);

  var aErrors = [];

  // Handle pre-existing link errors
  for (let index = 0; index < values.length; index++) {
    let newFileLocation = values[index].split(fmLinkBucket)[1];
    let result = await filesAdapter.fetchIndividualFileLocation(
      newFileLocation
    );

    if (result.length > 0)
      aErrors.push(`${newFileLocation}: File already exists.`);
  }

  // Catch-all for any other errors
  if (aErrors.length === 0) {
    for (let index = 0; index < values.length; index++) {
      let newFileLocation = values[index].split(fmLinkBucket)[1];
      let result = await filesAdapter.postNewFileLocation(newFileLocation, {});

      let { success } = result;
      if (!success) aErrors.push(`${newFileLocation}: ${result}`);
    }
  }

  if (aErrors.length > 0)
    finalReport = {
      type: "ALERT_DOCK",
      title: `There was an issue updating the File Manager links!`,
      message: `${aErrors.map((error) => `${error}\n`)}`,
    };
  else
    finalReport = {
      type: "TOAST_MESSAGE",
      message: `File Manager links were successfully updated!`,
    };

  store.dispatch(closeDock());
  store.dispatch(clearFMLinks());

  return finalReport;
};
